/**
 * Plans side by side — one table for every comparison (owner, 18 Oct 2026: "it still feels a bit
 * bland"). Used by "All the plans, side by side" on step 3 and "Two homes, side by side" on Home.
 *
 * What makes it read: a soft header band; the headline column (left to the family) set large in
 * navy with its gain against doing nothing beneath; a small emerald mark on the best value in each
 * column, so the eye can scan down for "best"; a thin amber bar under "tied up in the deposit" so
 * the trade-off is visible, not just read; the doing-nothing row muted and labelled; the chosen row
 * with its green rail and chip; a hover on every row that opens. White throughout, colour as accent
 * (no pastel infills — owner, 15 Oct 2026).
 */
import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';
import { useNarrowScreen } from '../../utils/useNarrowScreen.ts';

export interface ComparisonColumn {
    key: string;
    label: string;
    /** Which way is better, for the "best" mark; omit for columns with no better. */
    best?: 'max' | 'min';
    /** The headline column: larger, navy, with the delta beneath. */
    headline?: boolean;
    /** Draw a thin bar under the figure, scaled to the column's largest value. */
    bar?: 'amber' | 'emerald';
    /** Words, not a figure. */
    text?: boolean;
}

export interface ComparisonCell {
    /** The figure the "best" mark and the bar are judged on. Null = not applicable. */
    value: number | null;
    /** What is shown; defaults to the formatted value. */
    display?: React.ReactNode;
    /** The line under the figure — "+$65,387 vs doing nothing". */
    sub?: React.ReactNode;
    tone?: 'good' | 'bad' | 'neutral';
}

export interface ComparisonRow {
    key: string;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    chosen?: boolean;
    /** Doing nothing: muted, never "best", no bar. */
    baseline?: boolean;
    onOpen?: () => void;
    cells: Record<string, ComparisonCell>;
}

export interface ComparisonTableProps {
    columns: ComparisonColumn[];
    rows: ComparisonRow[];
    /** The first column's heading — "Plan" or "Plan · home". */
    firstHeading?: string;
    testId?: string;
    /** Under the table: what "best" means. */
    legend?: boolean;
}

const BestMark = () => (
    <span className="ml-1.5 inline-block h-2 w-2 translate-y-[-1px] rounded-full bg-emerald-500 align-middle" aria-label="best in this column" title="Best in this column" />
);

const ComparisonTable = ({ columns, rows, firstHeading = 'Plan', testId, legend = true }: ComparisonTableProps) => {
    // The best value per column, judged over the plans only (never doing nothing).
    const best: Record<string, number | null> = {};
    const maxAbs: Record<string, number> = {};
    for (const c of columns) {
        const vals = rows.filter((r) => !r.baseline).map((r) => r.cells[c.key]?.value).filter((v): v is number => v != null && Number.isFinite(v));
        best[c.key] = c.best && vals.length > 1 ? (c.best === 'max' ? Math.max(...vals) : Math.min(...vals)) : null;
        maxAbs[c.key] = Math.max(0, ...rows.map((r) => Math.abs(r.cells[c.key]?.value ?? 0)));
    }
    const anyBest = columns.some((c) => best[c.key] != null);
    const narrow = useNarrowScreen();
    const isBestCell = (r: ComparisonRow, c: ComparisonColumn) => {
        const cell = r.cells[c.key];
        return !r.baseline && best[c.key] != null && cell?.value != null && Math.round(cell.value) === Math.round(best[c.key] as number);
    };
    /*
     * ON A PHONE: one card per plan (owner-approved polish, 25 Oct 2026). The table's plan names
     * wrapped a word a line and two columns fell off the screen; a card shows the name, the chosen
     * or comparison mark, and every column's figure in two rows. Laptop and iPad keep the table.
     */
    if (narrow) {
        return (
            <div className="space-y-3" data-testid={testId}>
                {rows.map((r) => (
                    <div
                        key={r.key}
                        onClick={r.onOpen}
                        className={`rounded-xl border bg-white px-4 py-3.5 ${r.chosen ? 'border-emerald-500' : 'border-slate-200'} ${r.onOpen ? 'cursor-pointer' : ''}`}
                    >
                        <div className="flex flex-wrap items-center gap-2">
                            <span className={`${r.baseline ? 'text-slate-600' : 'font-semibold text-slate-900'}`}>{r.title}</span>
                            {r.chosen && <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-emerald-700"><CheckCircle2 size={13} strokeWidth={2} aria-hidden />Chosen</span>}
                            {r.baseline && <span className="text-[11.5px] font-semibold text-slate-500">the comparison</span>}
                        </div>
                        {r.subtitle && <p className="mt-0.5 text-[12px] text-slate-500">{r.subtitle}</p>}
                        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
                            {columns.map((c) => {
                                const cell = r.cells[c.key];
                                if (!cell) return null;
                                const toneClass = cell.tone === 'good' ? 'text-emerald-700' : cell.tone === 'bad' ? 'text-rose-700' : r.baseline ? 'text-slate-600' : 'text-slate-900';
                                return (
                                    <div key={c.key} className={c.text ? 'col-span-2' : ''}>
                                        <dt className="text-[12px] text-slate-500">{c.label}</dt>
                                        <dd className={`tabular-nums ${c.text ? 'text-[13px] text-slate-600' : c.headline ? 'text-[16px] font-bold text-brand-navy' : `font-semibold ${toneClass}`}`}>
                                            {c.text ? cell.display : (cell.display ?? (cell.value != null ? formatCurrency(Math.round(cell.value)) : '—'))}
                                            {!c.text && isBestCell(r, c) && <BestMark />}
                                            {cell.sub && <span className="block text-[12px] font-semibold">{cell.sub}</span>}
                                        </dd>
                                    </div>
                                );
                            })}
                        </dl>
                    </div>
                ))}
                {legend && anyBest && (
                    <p className="text-[12px] text-slate-500"><span className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle" aria-hidden /> the best figure in that column, among the plans.</p>
                )}
            </div>
        );
    }
    return (
        <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full border-collapse text-[14px]" data-testid={testId}>
                <thead>
                    <tr className="border-b border-slate-200 text-left text-[12.5px] font-semibold text-slate-500">
                        <th className="px-4 py-3">{firstHeading}</th>
                        {columns.map((c) => (
                            <th key={c.key} className={`px-3 py-3 ${c.text ? '' : 'text-right'}`}>{c.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {rows.map((r) => (
                        <tr
                            key={r.key}
                            onClick={r.onOpen}
                            className={`${r.onOpen ? 'cursor-pointer hover:bg-blue-50/40' : ''} transition-colors ${r.baseline ? 'bg-slate-50/50' : 'bg-white'}`}
                        >
                            <td className={`px-4 py-3.5 align-top ${r.chosen ? 'shadow-[inset_4px_0_0_#059669]' : ''}`}>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className={`${r.baseline ? 'text-slate-600' : 'font-semibold text-slate-900'}`}>{r.title}</span>
                                    {r.chosen && <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-emerald-700"><CheckCircle2 size={13} strokeWidth={2} aria-hidden />Chosen</span>}
                                    {r.baseline && <span className="text-[11.5px] font-semibold text-slate-500">the comparison</span>}
                                </div>
                                {r.subtitle && <p className="mt-0.5 text-[12px] text-slate-500">{r.subtitle}</p>}
                            </td>
                            {columns.map((c) => {
                                const cell = r.cells[c.key];
                                if (!cell) return <td key={c.key} className="px-3 py-3.5" />;
                                const isBest = !r.baseline && best[c.key] != null && cell.value != null && Math.round(cell.value) === Math.round(best[c.key] as number);
                                const toneClass = cell.tone === 'good' ? 'text-emerald-700' : cell.tone === 'bad' ? 'text-rose-700' : r.baseline ? 'text-slate-600' : 'text-slate-900';
                                const pct = c.bar && maxAbs[c.key] > 0 && cell.value != null ? Math.max(2, Math.min(100, (Math.abs(cell.value) / maxAbs[c.key]) * 100)) : null;
                                return (
                                    <td key={c.key} className={`px-3 py-3.5 align-top ${c.text ? 'text-slate-600' : 'text-right tabular-nums'}`}>
                                        {c.text ? (
                                            <span>{cell.display}</span>
                                        ) : (
                                            <>
                                                <span className={`${c.headline ? (r.baseline ? 'text-[16px] font-semibold text-slate-600' : 'text-[17px] font-bold text-brand-navy') : `font-semibold ${toneClass}`}`}>
                                                    {cell.display ?? (cell.value != null ? formatCurrency(Math.round(cell.value)) : '—')}
                                                </span>
                                                {isBest && <BestMark />}
                                                {cell.sub && <span className="block whitespace-nowrap text-[12px] font-semibold">{cell.sub}</span>}
                                                {pct != null && !r.baseline && (
                                                    <span className="mt-1.5 ml-auto block h-1 w-20 overflow-hidden rounded-full bg-slate-100" aria-hidden>
                                                        <span className={`block h-full rounded-full ${c.bar === 'emerald' ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            {legend && anyBest && (
                <p className="border-t border-slate-100 bg-white px-4 py-2 text-[12px] text-slate-500"><span className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle" aria-hidden /> the best figure in that column, among the plans.</p>
            )}
        </div>
    );
};

export default ComparisonTable;
